import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import logo from '../assets/logo.svg';

const Container = styled.div`
  max-width: 600px;
  margin: 40px auto;
  padding: 20px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
`;

const Logo = styled.img`
  width: 200px;
  margin-bottom: 30px;
`;

const InfoCard = styled.div`
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 30px;
  margin: 20px 0;
  width: 100%;
  text-align: left;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #eee;
  
  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.span`
  font-weight: bold;
  color: #333;
  flex: 1;
`;

const InfoValue = styled.span`
  color: #666;
  flex: 2;
  text-align: right;
`;

const LoadingMessage = styled.div`
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 40px 20px;
  margin: 20px 0;
  width: 100%;
  text-align: center;
  font-size: 18px;
  color: #666;
`;

const ErrorMessage = styled.div`
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 8px;
  padding: 40px 20px;
  margin: 20px 0;
  width: 100%;
  text-align: center;
  font-size: 18px;
  color: #721c24;
`;

const Button = styled.button`
  width: 250px;
  padding: 12px 20px;
  background-color: #4CAF50;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  cursor: pointer;
  margin: 10px 0;

  &:hover {
    background-color: #45a049;
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const TableContainer = styled.div`
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 30px;
  margin: 20px 0;
  width: 100%;
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
  background-color: white;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
`;

const TableHeader = styled.th`
  background-color: #007bff;
  color: white;
  padding: 12px 8px;
  text-align: left;
  font-weight: bold;
  font-size: 14px;
  border-bottom: 2px solid #0056b3;
`;

const SortableHeader = styled(TableHeader)`
  cursor: pointer;
  user-select: none;
  position: relative;
  
  &:hover {
    background-color: #0056b3;
  }
  
  &:after {
    content: '${props => props.sortDirection === 'asc' ? '↑' : props.sortDirection === 'desc' ? '↓' : '⇅'}';
    position: absolute;
    right: 8px;
    opacity: ${props => props.isActive ? 1 : 0.5};
    font-size: 12px;
  }
`;

const TableRow = styled.tr`
  &:nth-child(even) {
    background-color: #f8f9fa;
  }

  &:hover {
    background-color: #e3f2fd;
  }
`;

const TableCell = styled.td`
  padding: 10px 8px;
  border-bottom: 1px solid #dee2e6;
  font-size: 13px;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 120px;
`;

const SectionTitle = styled.h3`
  color: #333;
  margin-bottom: 15px;
  font-size: 20px;
`;

const NoDataMessage = styled.div`
  background-color: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 8px;
  padding: 20px;
  margin: 20px 0;
  width: 100%;
  text-align: center;
  font-size: 16px;
  color: #856404;
`;

function UserInfoPanel() {
  const navigate = useNavigate();
  const [customerData, setCustomerData] = useState(null);
  const [energyData, setEnergyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [energyError, setEnergyError] = useState(null);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  useEffect(() => {
    fetchCustomerInfo();
    fetchEnergyData();
  }, []);

  const fetchCustomerInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/data/customer-info`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch customer information');
      }

      const data = await response.json();
      setCustomerData(data.data);
    } catch (error) {
      setError(error.message);
    }
  };

  const fetchEnergyData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/data/energy-data`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        if (response.status === 404) {
          // No energy data found - this is not an error, just no data
          setEnergyData([]);
          return;
        }
        throw new Error('Failed to fetch energy data');
      }

      const data = await response.json();
      setEnergyData(data.data || []);
    } catch (error) {
      setEnergyError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToDashboard = () => {
    navigate('/dashboard');
  };

  const handleBackToConfirmation = () => {
    navigate('/confirm');
  };

  // Sorting functions
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortData = (data) => {
    if (!sortField || !data) return data;

    return [...data].sort((a, b) => {
      let aVal, bVal;

      switch (sortField) {
        case 'scenario':
          aVal = a.scenario || '';
          bVal = b.scenario || '';
          break;
        case 'billing_period_start':
          aVal = new Date(a.billingPeriodStartISO || a.billing_period_start || 0);
          bVal = new Date(b.billingPeriodStartISO || b.billing_period_start || 0);
          break;
        case 'billing_period_end':
          aVal = new Date(a.billingPeriodEndISO || a.billing_period_end || 0);
          bVal = new Date(b.billingPeriodEndISO || b.billing_period_end || 0);
          break;
        case 'total_usage':
          aVal = parseFloat(a.total_usage || 0);
          bVal = parseFloat(b.total_usage || 0);
          break;
        case 'amount_due':
          // Extract numeric value from formatted string (e.g., "$123.45" -> 123.45)
          aVal = parseFloat((a.rawAmount || a.amount_due || '0').toString().replace(/[$,]/g, ''));
          bVal = parseFloat((b.rawAmount || b.amount_due || '0').toString().replace(/[$,]/g, ''));
          break;
        default:
          return 0;
      }

      // Handle date comparisons
      if (aVal instanceof Date && bVal instanceof Date) {
        const comparison = aVal.getTime() - bVal.getTime();
        return sortDirection === 'asc' ? comparison : -comparison;
      }

      // Handle string comparisons
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        const comparison = aVal.localeCompare(bVal);
        return sortDirection === 'asc' ? comparison : -comparison;
      }

      // Handle numeric comparisons
      const comparison = aVal - bVal;
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };

  const formatFieldName = (key) => {
    // Convert camelCase to readable format
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .replace(/(\d+)$/, ' $1'); // Add space before numbers
  };

  const renderCustomerInfo = () => {
    if (!customerData) return null;

    // Define the desired field order
    const fieldOrder = [
      'customerName',
      'address',
      'accountNumber'
    ];

    // Get all meter number and scenario fields dynamically and sort them
    const allFields = Object.keys(customerData);
    
    // Get unique meter numbers from the fields
    const meterNumbers = [...new Set(
      allFields
        .filter(field => field.match(/^(meterNumber|scenario)\d+$/))
        .map(field => parseInt(field.match(/\d+/)?.[0] || '0'))
        .filter(num => num > 0)
    )].sort((a, b) => a - b);
    
    // Build ordered meter and scenario fields
    const meterFields = [];
    meterNumbers.forEach(num => {
      if (allFields.includes(`meterNumber${num}`)) {
        meterFields.push(`meterNumber${num}`);
      }
      if (allFields.includes(`scenario${num}`)) {
        meterFields.push(`scenario${num}`);
      }
    });

    // Combine ordered fields with meter fields
    const orderedFields = [...fieldOrder, ...meterFields];

    // Get remaining fields that aren't in the ordered list
    // const remainingFields = allFields.filter(field => 
    //   !orderedFields.includes(field) && 
    //   customerData[field] !== null && 
    //   customerData[field] !== undefined && 
    //   customerData[field] !== ''
    // );

    // Combine all fields in the desired order
    //const finalFieldOrder = [...orderedFields, ...remainingFields];
    const finalFieldOrder = [...orderedFields];

    return finalFieldOrder
      .filter(key => 
        customerData[key] !== null && 
        customerData[key] !== undefined && 
        customerData[key] !== ''
      )
      .map((key) => (
        <InfoRow key={key}>
          <InfoLabel>{formatFieldName(key)}:</InfoLabel>
          <InfoValue>{customerData[key]}</InfoValue>
        </InfoRow>
      ));
  };

  const renderEnergyTable = () => {
    if (!energyData || energyData.length === 0) {
      return (
        <NoDataMessage>
          No energy usage data available. 
          <br />
          Please request usage summary data from the data request page first.
        </NoDataMessage>
      );
    }

    // Apply sorting to the data
    const sortedData = sortData(energyData);

    return (
      <Table>
        <thead>
          <tr>
            <TableHeader>Usage Point ID</TableHeader>
            <SortableHeader 
              onClick={() => handleSort('scenario')}
              isActive={sortField === 'scenario'}
              sortDirection={sortField === 'scenario' ? sortDirection : null}
            >
              Scenario
            </SortableHeader>
            <SortableHeader 
              onClick={() => handleSort('billing_period_start')}
              isActive={sortField === 'billing_period_start'}
              sortDirection={sortField === 'billing_period_start' ? sortDirection : null}
            >
              Period Start
            </SortableHeader>
            <SortableHeader 
              onClick={() => handleSort('billing_period_end')}
              isActive={sortField === 'billing_period_end'}
              sortDirection={sortField === 'billing_period_end' ? sortDirection : null}
            >
              Period End
            </SortableHeader>
            <TableHeader>Days</TableHeader>
            <TableHeader>Currency</TableHeader>
            <SortableHeader 
              onClick={() => handleSort('total_usage')}
              isActive={sortField === 'total_usage'}
              sortDirection={sortField === 'total_usage' ? sortDirection : null}
            >
              Total Usage
            </SortableHeader>
            <TableHeader>Usage Unit</TableHeader>
            <SortableHeader 
              onClick={() => handleSort('amount_due')}
              isActive={sortField === 'amount_due'}
              sortDirection={sortField === 'amount_due' ? sortDirection : null}
            >
              Amount Due
            </SortableHeader>
          </tr>
        </thead>
        <tbody>
          {sortedData.map((row, index) => (
            <TableRow key={index}>
              <TableCell title={row.usagePointId}>{row.usagePointId}</TableCell>
              <TableCell title={row.scenario}>{row.scenario}</TableCell>
              <TableCell>{row.billing_period_start}</TableCell>
              <TableCell>{row.billing_period_end}</TableCell>
              <TableCell>{row.days}</TableCell>
              <TableCell>{row.currency}</TableCell>
              <TableCell>{row.total_usage}</TableCell>
              <TableCell>{row.usage_unit}</TableCell>
              <TableCell>{row.amount_due}</TableCell>
            </TableRow>
          ))}
        </tbody>
      </Table>
    );
  };

  if (loading) {
    return (
      <Container>
        <Logo src={logo} alt="Logo" />
        <h2>Customer & Energy Information</h2>
        <LoadingMessage>
          Loading customer and energy information...
        </LoadingMessage>
        <Button onClick={handleBackToDashboard}>
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Logo src={logo} alt="Logo" />
        <h2>Customer & Energy Information</h2>
        <ErrorMessage>
          Error loading customer information: {error}
        </ErrorMessage>
        <Button onClick={handleBackToDashboard}>
          Back to Dashboard
        </Button>
        <Button onClick={handleBackToConfirmation}>
          Back to Confirmation
        </Button>
      </Container>
    );
  }

  return (
    <Container>
      <Logo src={logo} alt="Logo" />
      
      <h2>Customer & Energy Information</h2>
      
      {/* Customer Information Section */}
      <SectionTitle>Customer Information</SectionTitle>
      {customerData ? (
        <InfoCard>
          {renderCustomerInfo()}
        </InfoCard>
      ) : (
        <ErrorMessage>
          No customer information available
        </ErrorMessage>
      )}
      
      {/* Energy Usage Data Section */}
      <SectionTitle>Energy Usage Data</SectionTitle>
      {energyError ? (
        <ErrorMessage>
          Error loading energy data: {energyError}
        </ErrorMessage>
      ) : (
        <TableContainer>
          {renderEnergyTable()}
        </TableContainer>
      )}
      
      <Button onClick={handleBackToDashboard}>
        Back to Dashboard
      </Button>
      
      <Button onClick={handleBackToConfirmation}>
        Back to Confirmation
      </Button>
    </Container>
  );
}

export default UserInfoPanel;
